import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface AgentLedgerFilterDto {
  agentId?: string;
  agentName?: string;
  fromTransactionDate?: string;
  toTransactionDate?: string;
  entryType?: string;
  transactionType?: string; // "Debit", "Credit", or "All"
  pageIndex: number;
  pageSize: number;
}

export interface AgentLedgerDto {
  id: string;
  agentId: string;
  agentName: string;
  entryType: string;
  description?: string;
  debitAmount: number;
  creditAmount: number;
  openingBalance: number;
  closingBalance: number;
  transactionDate: string;
  referenceId?: string;
  referenceType?: string;
  paymentMode?: string;
  status?: string;
  invoiceStatus?: string;
  isCancelled?: boolean;
  cancellationEntryId?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface AgentLedgerPeriodSummaryDto {
  agentId?: string;
  agentName?: string;
  openingBalance: number;
  closingBalance: number;
  totalDebit: number;
  totalCredit: number;
  totalCount: number;
  netChange: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  summary?: AgentLedgerPeriodSummaryDto;
}

export interface InvoicePaymentAllocationDto {
  invoiceId: string;
  amount: number;
}

export interface RecordAgentPaymentDto {
  agentId: string;
  creditAmount: number;
  description?: string;
  transactionDate?: string;
  referenceId?: string;
  referenceType?: string;
  paymentMode?: string;
  invoiceAllocations?: InvoicePaymentAllocationDto[];
}

export interface CancelAgentLedgerDto {
  agentId: string;
  ledgerEntryId: string;
  reason?: string;
  transactionDate?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AgentLedgerService {
  private readonly apiUrl = `${environment.apiConfig.uri}/api/AgentLedger`;

  constructor(private http: HttpClient) {}

  getLedgerEntries(filter: AgentLedgerFilterDto): Observable<PaginatedResponse<AgentLedgerDto>> {
    let params = new HttpParams();

    const filterEntries: [string, string | number | undefined][] = [
      ['AgentId', filter.agentId],
      ['AgentName', filter.agentName],
      ['FromTransactionDate', filter.fromTransactionDate],
      ['ToTransactionDate', filter.toTransactionDate],
      ['EntryType', filter.entryType],
      ['TransactionType', filter.transactionType ?? 'All'],
      ['PageIndex', filter.pageIndex],
      ['PageSize', filter.pageSize],
    ];

    for (const [key, value] of filterEntries) {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    }

    return this.http.get<PaginatedResponse<AgentLedgerDto>>(this.apiUrl, { params });
  }

  recordPayment(dto: RecordAgentPaymentDto): Observable<AgentLedgerDto> {
    return this.http.post<AgentLedgerDto>(`${this.apiUrl}/payment`, dto);
  }

  cancelLedgerEntry(dto: CancelAgentLedgerDto): Observable<AgentLedgerDto> {
    return this.http.post<AgentLedgerDto>(`${this.apiUrl}/cancel`, dto);
  }
}
